import React, { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Loader2, Unlink } from "lucide-react";
import { toast } from "sonner";

const LinkedList = ({ linked, onUnlink, busy }) => !linked.length ? null : (
  <div className="space-y-1" data-testid="link-current-list">
    <Label className="text-xs font-bold uppercase tracking-widest">Ligadas actualmente</Label>
    {linked.map((l) => (
      <div key={l.id} className="flex items-center justify-between rounded-sm border border-border px-3 py-1.5 text-sm">
        <span className="font-mono text-xs font-bold text-primary">{l.number}</span>
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-destructive" disabled={busy} onClick={() => onUnlink(l)} data-testid={`link-unlink-${l.number}`}><Unlink className="h-3.5 w-3.5" /> Desligar</Button>
      </div>))}
  </div>
);

const LinesTable = ({ lines, setQty }) => (
  <div className="rounded-sm border border-border divide-y divide-border" data-testid="link-lines">
    {lines.filter((l) => l.pending > 0).map((l) => (
      <div key={l.index} className="flex items-center gap-2 px-3 py-1.5 text-xs">
        <span className="flex-1 min-w-0 truncate">{l.code && <span className="font-mono mr-1">{l.code}</span>}{l.description}</span>
        <span className="text-muted-foreground whitespace-nowrap">pend. {l.pending}</span>
        <Input type="number" min={0} max={l.pending} step="any" className="h-7 w-20 rounded-sm text-right text-xs" value={l.qty}
          onChange={(e) => setQty(l.index, e.target.value)} data-testid={`link-qty-${l.index}`} />
      </div>))}
  </div>
);

// Modo factura: invoice={...}  ·  Modo cotización: quote={...}
export const LinkQuoteInvoiceDialog = ({ invoice, quote, onClose, onDone }) => {
  const doc = invoice || quote;
  const [data, setData] = useState({ linked: [], options: [] });
  const [pick, setPick] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const iid = invoice ? invoice.id : pick;
  const qid = quote ? quote.id : pick;

  const loadOpts = () => {
    const url = invoice ? `/invoices/${invoice.id}/linkable-quotes` : `/quotations/${quote.id}/linkable-invoices`;
    api.get(url).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  };
  useEffect(() => { if (doc) { setPick(""); setLines([]); loadOpts(); } }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pick) { setLines([]); return; }
    api.get(`/invoices/${iid}/quote-suggest`, { params: { quotation_id: qid } })
      .then((r) => setLines(r.data.map((l) => ({ ...l, qty: l.suggested })))).catch(() => setLines([]));
  }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${iid}/link-quote`, { quotation_id: qid, items: lines.map((l) => ({ index: l.index, qty: Number(l.qty || 0) })) });
      toast.success(r.data.message); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unlink = async (l) => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice ? invoice.id : l.id}/unlink-quote`, { quotation_id: invoice ? l.id : quote.id });
      toast.success(`${l.number} desligada`); loadOpts(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const opts = data.options.map((o) => ({ value: o.id, label: `${o.number}${o.fel_series ? ` (${o.fel_series}-${o.fel_number})` : ""} · ${o.date || ""} · ${money(o.currency || "GTQ", o.total || 0)}${o.status ? ` · ${o.status}` : ""}` }));

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto" data-testid="link-quote-invoice-dialog">
        <DialogHeader><DialogTitle className="font-heading">{invoice ? `Ligar cotización a ${invoice.number}` : `Ligar ${quote?.number} a una factura`}</DialogTitle>
          <DialogDescription>Se marca como facturado solo lo que indiques (sugerido según los productos que coinciden). Lo demás queda pendiente.</DialogDescription></DialogHeader>
        <LinkedList linked={data.linked} onUnlink={unlink} busy={busy} />
        {data.note && <p className="text-xs text-muted-foreground">{data.note}</p>}
        <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">{invoice ? "Cotización" : "Factura"}</Label>
          <SearchableSelect value={pick} onChange={setPick} testid="link-pick" placeholder={opts.length ? "Selecciona..." : "No hay documentos disponibles"} searchPlaceholder="Buscar..." options={opts} /></div>
        {pick && (lines.length ? <LinesTable lines={lines} setQty={(i, v) => setLines((ls) => ls.map((l) => l.index === i ? { ...l, qty: v } : l))} />
          : <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>)}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={onClose}>Cerrar</Button>
          <Button className="rounded-sm font-bold" disabled={busy || !pick} onClick={save} data-testid="link-save">{busy ? "Guardando..." : "Ligar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
